import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, RefreshCw, Receipt, Info } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { saveBlob } from "@/components/admin/PointsUploadPanel";
import { PageLoader, LoadError, EmptyState } from "@/components/admin/LoadState";
import { fmtDate, fmtMoney, fmtNum } from "@/lib/format";

export const INTERVAL: Record<string, string> = { month: "Mensual", year: "Anual" };
export const PERIOD_KIND: Record<string, string> = { alta: "Alta", renovacion: "Renovación", cambio_plan: "Cambio de plan", ajuste: "Ajuste por subir de plan", otro: "Otro" };
export const CHANNEL: Record<string, string> = { stripe: "Tarjeta", debito: "Débito Armony" };
export const PERIOD_STATUS: Record<string, { label: string; cls: string }> = {
  pagado: { label: "Pagado", cls: "text-emerald-800" },
  pendiente: { label: "Pendiente", cls: "text-amber-800" },
  fallido: { label: "Pago fallido", cls: "text-red-700" },
  incobrable: { label: "Incobrable", cls: "text-red-700" },
  anulado: { label: "Anulado", cls: "text-slate-600" },
};

// Conciliación: periodos cobrados (licencias vendidas) por rango de fechas, con totales por plan y modalidad.
// Cada fila guarda el precio y la vigencia de su momento: no cambia si después cambian las tarifas.
export function SubscriptionLedger() {
  const { user } = useAuth();
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [plan, setPlan] = useState("");
  const [canal, setCanal] = useState("");
  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const params = () => ({ ...(desde && { desde }), ...(hasta && { hasta }), ...(plan && { plan }), ...(canal && { canal }) });
  const load = () => {
    setFailed(false);
    return api.get("/admin/subscriptions/periods", { params: params() }).then((r) => {
      setData(r.data);
      if (!desde) setDesde(r.data.desde);
      if (!hasta) setHasta(r.data.hasta);
    }).catch(() => setFailed(true));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [plan, canal]);

  const download = async () => {
    try {
      const { data: blob } = await api.get("/admin/subscriptions/periods.csv", { params: params(), responseType: "blob" });
      saveBlob(blob, `licencias_${desde}_${hasta}.csv`);
    } catch { toast.error("No se pudo descargar el archivo"); }
  };

  const sync = async () => {
    try {
      const { data: r } = await api.post("/admin/subscriptions/sync-stripe");
      toast.success(`Listo: ${fmtNum(r.subscriptions)} suscripciones y ${fmtNum(r.invoices)} facturas revisadas${r.errors ? `; ${fmtNum(r.errors)} con error (revisa los registros)` : ""}.`);
      await load();
    } catch (e) { toast.error(apiError(e)); throw e; }
  };

  return (
    <div className="space-y-5" data-testid="subscription-ledger">
      <form className="card-obsidian p-5 flex flex-wrap items-end gap-4" onSubmit={(e) => { e.preventDefault(); load(); }}>
        <div>
          <Label htmlFor="ledger-desde" className="text-sm text-slate-700">Periodos que inician desde</Label>
          <Input id="ledger-desde" type="date" value={desde} onChange={(e: any) => setDesde(e.target.value)} className="mt-1.5 h-10 w-44" data-testid="ledger-desde" />
        </div>
        <div>
          <Label htmlFor="ledger-hasta" className="text-sm text-slate-700">hasta</Label>
          <Input id="ledger-hasta" type="date" value={hasta} onChange={(e: any) => setHasta(e.target.value)} className="mt-1.5 h-10 w-44" data-testid="ledger-hasta" />
        </div>
        <div>
          <Label htmlFor="ledger-plan" className="text-sm text-slate-700">Plan</Label>
          <select id="ledger-plan" value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="ledger-plan"
            className="mt-1.5 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 block">
            <option value="">Todos</option>
            <option value="fitrewards">Armony Contigo Plus</option>
            <option value="fitrewards_plus">Armony Contigo Premium</option>
          </select>
        </div>
        <div>
          <Label htmlFor="ledger-canal" className="text-sm text-slate-700">Cobro</Label>
          <select id="ledger-canal" value={canal} onChange={(e) => setCanal(e.target.value)} data-testid="ledger-canal"
            className="mt-1.5 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 block">
            <option value="">Tarjeta y débito</option>
            <option value="stripe">Tarjeta</option>
            <option value="debito">Débito Armony</option>
          </select>
        </div>
        <Button type="submit" className="gold-gradient-bg text-white rounded-full h-10" data-testid="ledger-apply">Ver periodo</Button>
        <div className="flex gap-2 ml-auto">
          {user?.role === "admin" && (
            <Button type="button" variant="outline" onClick={() => setSyncing(true)} className="rounded-full h-10 border-slate-200 text-slate-700" data-testid="ledger-sync">
              <RefreshCw className="w-4 h-4 mr-1" aria-hidden="true" /> Completar desde Stripe
            </Button>
          )}
          <Button type="button" variant="outline" onClick={download} disabled={!data?.items?.length} className="rounded-full h-10 border-slate-200 text-slate-700" data-testid="ledger-download">
            <Download className="w-4 h-4 mr-1" aria-hidden="true" /> Descargar CSV
          </Button>
        </div>
      </form>

      {failed ? (
        <LoadError what="los cobros" onRetry={load} />
      ) : !data ? (
        <PageLoader />
      ) : data.items.length === 0 ? (
        <EmptyState icon={Receipt}>
          <span data-testid="ledger-empty">No hay periodos cobrados que inicien entre el {fmtDate(`${data.desde}T12:00:00`)} y el {fmtDate(`${data.hasta}T12:00:00`)}.</span>
        </EmptyState>
      ) : (
        <>
          <section className="card-obsidian overflow-hidden" aria-labelledby="ledger-summary-title">
            <h2 id="ledger-summary-title" className="px-5 pt-5 pb-3 text-lg font-semibold">Licencias y montos por plan</h2>
            <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Resumen por plan">
              <table className="w-full text-sm min-w-[760px]" data-testid="ledger-summary">
                <thead>
                  <tr className="border-y border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
                    <th scope="col" className="text-left font-medium px-5 py-3">Plan</th>
                    <th scope="col" className="text-left font-medium px-5 py-3">Modalidad</th>
                    <th scope="col" className="text-right font-medium px-5 py-3">Licencias pagadas</th>
                    <th scope="col" className="text-right font-medium px-5 py-3">Altas · renov. · cambios</th>
                    <th scope="col" className="text-right font-medium px-5 py-3">Facturado</th>
                    <th scope="col" className="text-right font-medium px-5 py-3">Cobrado</th>
                    <th scope="col" className="text-right font-medium px-5 py-3">Por cobrar</th>
                  </tr>
                </thead>
                <tbody>
                  {data.summary.map((g: any) => (
                    <tr key={`${g.plan}-${g.plan_interval}`} className="border-b border-slate-100">
                      <td className="px-5 py-3 font-medium">{g.plan_name}</td>
                      <td className="px-5 py-3 text-slate-700">{INTERVAL[g.plan_interval] || "—"}</td>
                      <td className="px-5 py-3 text-right font-mono">{fmtNum(g.licencias)}</td>
                      <td className="px-5 py-3 text-right font-mono text-slate-600">{fmtNum(g.altas)} · {fmtNum(g.renovaciones)} · {fmtNum(g.cambios)}</td>
                      <td className="px-5 py-3 text-right font-mono">{fmtMoney(g.facturado)}</td>
                      <td className="px-5 py-3 text-right font-mono text-emerald-800">{fmtMoney(g.cobrado)}</td>
                      <td className={`px-5 py-3 text-right font-mono ${g.pendiente ? "text-amber-800" : "text-slate-600"}`}>{fmtMoney(g.pendiente)}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold bg-slate-50" data-testid="ledger-totals">
                    <td className="px-5 py-3" colSpan={2}>Total</td>
                    <td className="px-5 py-3 text-right font-mono">{fmtNum(data.totals.licencias)}</td>
                    <td />
                    <td className="px-5 py-3 text-right font-mono">{fmtMoney(data.totals.facturado)}</td>
                    <td className="px-5 py-3 text-right font-mono text-emerald-800">{fmtMoney(data.totals.cobrado)}</td>
                    <td className="px-5 py-3 text-right font-mono">{fmtMoney(data.totals.pendiente)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="px-5 py-3 text-xs text-slate-600">“Facturado” incluye prorrateos e impuestos de cada factura. “Licencias pagadas” cuenta los periodos con pago confirmado; los ajustes por subir de plan suman en “cambios” pero no son licencias nuevas.</p>
          </section>

          <section className="card-obsidian overflow-hidden" aria-labelledby="ledger-detail-title">
            <h2 id="ledger-detail-title" className="px-5 pt-5 pb-3 text-lg font-semibold">Detalle de periodos cobrados</h2>
            <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Detalle de periodos">
              <PeriodsTable rows={data.items} showClient />
            </div>
          </section>
        </>
      )}

      <PolicyNote />

      <ConfirmDialog
        open={syncing}
        onOpenChange={setSyncing}
        testid="ledger-sync-confirm"
        title="¿Completar el registro desde Stripe?"
        description={<p>Se leerán de Stripe las suscripciones vigentes y sus facturas para completar fechas, precios y periodos cobrados. No se cobra ni se modifica nada en Stripe, y se puede repetir sin duplicar datos.</p>}
        confirmLabel="Completar registro"
        onConfirm={sync}
      />
    </div>
  );
}

export function PeriodsTable({ rows, showClient }: { rows: any[]; showClient?: boolean }) {
  return (
    <table className="w-full text-sm min-w-[860px]" data-testid="periods-table">
      <thead>
        <tr className="border-y border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
          {showClient && <th scope="col" className="text-left font-medium px-5 py-3">Cliente</th>}
          <th scope="col" className="text-left font-medium px-5 py-3">Plan</th>
          <th scope="col" className="text-left font-medium px-5 py-3">Movimiento</th>
          <th scope="col" className="text-left font-medium px-5 py-3">Vigencia</th>
          <th scope="col" className="text-right font-medium px-5 py-3">Precio del plan</th>
          <th scope="col" className="text-right font-medium px-5 py-3">Total factura</th>
          <th scope="col" className="text-right font-medium px-5 py-3">Cobrado</th>
          <th scope="col" className="text-left font-medium px-5 py-3">Estado</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p: any) => {
          const st = PERIOD_STATUS[p.status] || { label: p.status, cls: "text-slate-600" };
          return (
            <tr key={p.id} className="border-b border-slate-100 last:border-0" data-testid="period-row">
              {showClient && (
                <td className="px-5 py-3">
                  <Link to={`/admin/usuarios/${p.user_id}`} className="font-medium text-slate-900 hover:underline">{p.name || "—"}</Link>
                  <p className="text-xs text-slate-600">Cédula {p.cedula || "—"}</p>
                </td>
              )}
              <td className="px-5 py-3">{p.plan_name || p.plan || "—"}<p className="text-xs text-slate-600">{INTERVAL[p.plan_interval] || "—"} · {CHANNEL[p.channel] || "Tarjeta"}</p></td>
              <td className="px-5 py-3 text-slate-700">{PERIOD_KIND[p.kind] || p.kind}<p className="text-xs text-slate-600 font-mono">{p.invoice_number || p.reference || ""}</p></td>
              <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{fmtDate(p.period_start)} – {fmtDate(p.period_end)}</td>
              <td className="px-5 py-3 text-right font-mono">{fmtMoney(p.unit_price, p.currency)}</td>
              <td className="px-5 py-3 text-right font-mono">{fmtMoney(p.amount_total, p.currency)}</td>
              <td className="px-5 py-3 text-right font-mono">{fmtMoney(p.amount_paid, p.currency)}</td>
              <td className={`px-5 py-3 text-xs font-medium ${st.cls}`}>{st.label}{p.paid_at && <p className="text-slate-600 font-normal">{fmtDate(p.paid_at)}</p>}
                {p.failure_reason && p.status === "fallido" && <p className="text-slate-600 font-normal">{p.failure_reason}</p>}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const PolicyNote = () => (
  <section className="card-obsidian p-5 text-sm text-slate-700" aria-labelledby="policy-title" data-testid="subscription-policy">
    <h2 id="policy-title" className="font-semibold text-slate-900 flex items-center gap-2 mb-2"><Info className="w-4 h-4 text-navy" aria-hidden="true" /> Cómo se registran las suscripciones</h2>
    <ul className="list-disc pl-5 space-y-1">
      <li><strong>Vigencia:</strong> empieza el día de la contratación. Mensual: un mes calendario (del 21 de octubre al 21 de noviembre). Anual: un año.</li>
      <li><strong>Precio:</strong> se guarda el de la contratación. Si cambian las tarifas generales, las suscripciones existentes renuevan con su precio original.</li>
      <li><strong>Renovación:</strong> automática al vencer cada periodo; cada renovación queda como un periodo nuevo con su cobro.</li>
      <li><strong>Cambio de plan antes del vencimiento:</strong> inmediato y con prorrateo. En la misma modalidad la diferencia (a favor o en contra) va en la próxima factura; entre mensual y anual se cobra en el acto y empieza un periodo nuevo. El plan nuevo toma la tarifa vigente.</li>
      <li><strong>Cancelación:</strong> el plan sigue hasta el final del periodo pagado y no se renueva. Con un pago atrasado, termina de inmediato.</li>
      <li><strong>Débito automático de Armony:</strong> el plan se activa al pedirlo y su cobro queda pendiente hasta que Armony lo confirme. Subir de plan es inmediato y se cobra una sola vez la diferencia proporcional; bajar de plan o cambiar entre mensual y anual aplica desde el siguiente periodo.</li>
    </ul>
  </section>
);
